export const translations = {
  en: {
    landing: {
      hero: {
        title: 'MapSound',
        subtitle: 'Create Sound Layers Across the Real World',
        description: 'Transform streets, parks and landmarks into immersive audio experiences for your audience.',
        tryButton: 'Try MapSound',
        contactButton: 'Contact Us for a Project'
      },
      intro: {
        title: 'Turn Any Location Into a Sound Experience',
        description: 'With MapSound, you can draw zones or routes on a map, attach music or narration, and publish QR codes for visitors to experience sound in the real world — no speakers, no installations, no apps.'
      },
      keyFeatures: {
        title: 'Key Features',
        soundDrops: {
          title: 'Sound Drops',
          description: 'Place one or more sound moments in a precise location — visitors step in, and the audio plays.'
        },
        soundZones: {
          title: 'Sound Zones',
          description: 'Define areas on the map and curate playlists for everyone inside.'
        },
        soundTrails: {
          title: 'Sound Trails',
          description: 'Guide visitors along sequenced Sound Stops — each with its own track, story, or ambience.'
        },
        listeningRadius: {
          title: 'Listening Radius',
          description: 'Control how far your audio reaches. Choose from intimate 5m stops to wide 50m soundscapes.'
        },
        qrAccess: {
          title: 'QR Access (No Login)',
          description: 'Generate branded QR codes that launch instant playback for any Sound Zone or Stop.'
        }
      },
      howItWorks: {
        title: 'How It Works',
        step1: {
          number: '1',
          title: 'Draw',
          description: 'Create Sound Zones or Trails on the map.'
        },
        step2: {
          number: '2',
          title: 'Add Sound',
          description: 'Upload or link tracks and set their radius and order.'
        },
        step3: {
          number: '3',
          title: 'Share',
          description: 'Print QR codes and place them on-site. Visitors scan and listen instantly.'
        }
      },
      useCases: {
        title: 'Business Use Cases',
        tourism: {
          title: 'Tourism Boards',
          description: 'Turn landmarks into narrated soundwalks.'
        },
        fitness: {
          title: 'Fitness Brands',
          description: 'Design audio-guided running or walking routes.'
        },
        artCulture: {
          title: 'Art & Culture',
          description: 'Create citywide installations where sound meets place.'
        }
      },
      pricing: {
        title: 'Pricing Tiers',
        starter: {
          name: 'Starter',
          price: 'Free',
          feature1: '3 Sound Zones',
          feature2: '1 Sound Trail',
          feature3: '3 QR campaigns'
        },
        pro: {
          name: 'Pro',
          price: '$49',
          perMonth: '/mo',
          popular: 'Popular',
          feature1: '20 Sound Zones',
          feature2: '5 Sound Trails',
          feature3: 'Custom QR branding',
          feature4: 'Analytics'
        },
        business: {
          name: 'Business',
          price: '$199',
          perMonth: '/mo',
          feature1: '50 Sound Zones',
          feature2: '15 Sound Trails',
          feature3: 'Multilingual support',
          feature4: 'API access'
        }
      },
      vision: {
        title: 'When Every Place Has Its Own Sound...',
        subtitle: '...the city becomes a Sonic City.',
        description: 'MapSound is the tool to get there.'
      },
      cta: {
        title: 'Ready to map your first sound?',
        button: 'Start Creating'
      },
      footer: {
        links: {
          privacy: 'Privacy',
          terms: 'Terms',
          contact: 'Contact'
        },
        copyright: '© 2024 MapSound. All rights reserved.'
      },
      features: {
        title: 'How It Works',
        subtitle: 'Everything you need to create your musical journey',
        pinMusic: {
          title: 'Pin Your Music',
          description: 'Drop a pin anywhere on the map and attach your favorite songs from YouTube. Every location tells a story through sound.'
        },
        discover: {
          title: 'Discover Sounds',
          description: 'Explore what others are listening to around the world. From local favorites to hidden gems, discover new music tied to real places.'
        },
        share: {
          title: 'Share & Connect',
          description: 'Build your musical journey and share it with friends. Create a soundtrack map of your travels, memories, and favorite places.'
        }
      },
      social: {
        title: 'Join the Community',
        description: 'Thousands of music lovers are already mapping their favorite sounds. Be part of a global community that celebrates music and places.',
        stats: {
          users: 'Active Users',
          pins: 'Music Pins',
          countries: 'Countries'
        }
      },
      cta: {
        title: 'Ready to Map Your Music?',
        description: 'Start your musical journey today. It\'s free, fun, and connects you to sounds from around the world.',
        button: 'Create Your Map'
      },
      howItWorks: {
        title: 'Simple. Powerful. Global.',
        step1: {
          title: 'Click the Map',
          description: 'Choose any location that means something to you'
        },
        step2: {
          title: 'Add Your Song',
          description: 'Paste a YouTube link and add your story'
        },
        step3: {
          title: 'Share & Discover',
          description: 'Explore music from around the world'
        }
      }
    },
    footer: {
      copyright: '© 2025 MapSound. Your music, your world — made with ❤️ in Los Angeles.'
    },
    auth: {
      backHome: 'Back to Home',
      title: 'MapSound',
      subtitle: 'Create Sound Layers Across the Real World',
      username: 'Username',
      email: 'Email',
      password: 'Password',
      signIn: 'Sign In',
      signUp: 'Sign Up',
      loading: 'Loading...',
      hasAccount: 'Already have an account? Sign in',
      noAccount: "Don't have an account? Sign up"
    },
    header: {
      search: 'Search locations...',
      logout: 'Logout'
    },
    sidebar: {
      map: 'Map',
      myPins: 'My Drops',
      discover: 'Discover',
      nearby: 'Nearby'
    },
    createPin: {
      title: 'Create Sound Drop',
      location: 'Location',
      pinTitle: 'Title',
      titlePlaceholder: 'Name this sound drop',
      description: 'Description',
      descriptionPlaceholder: 'Describe this sound drop...',
      youtubeUrl: 'YouTube URL',
      youtubeUrlPlaceholder: 'https://www.youtube.com/watch?v=...',
      tags: 'Tags',
      tagsPlaceholder: 'rock, jazz, ambient (comma separated)',
      cancel: 'Cancel',
      create: 'Create Drop',
      creating: 'Creating...'
    },
    editPin: {
      title: 'Edit Sound Drop',
      update: 'Update Drop',
      updating: 'Updating...',
      delete: 'Delete Drop',
      deleting: 'Deleting...',
      confirmDelete: 'Are you sure you want to delete this sound drop?'
    },
    map: {
      loading: 'Loading audio pins...',
      error: 'Failed to load map'
    },
    myPins: {
      title: 'My Sound Drops',
      empty: 'You haven\'t created any sound drops yet. Click on the map to create your first one!',
      plays: 'plays'
    },
    discover: {
      title: 'Discover Sound Drops',
      empty: 'No sound drops found. Be the first to create one!',
      plays: 'plays'
    },
    nearby: {
      title: 'Nearby Sound Drops',
      empty: 'No sound drops nearby. Walk around to discover music!',
      distance: 'away',
      enableLocation: 'Enable Location',
      locationDisabled: 'Location access is disabled',
      locationUnavailable: 'Location unavailable',
      searchingLocation: 'Searching for your location...',
      within50m: 'Within 50m'
    }
  },
  ja: {
    landing: {
      hero: {
        title1: 'ビートを',
        title2: 'マップに',
        title3: 'ドロップ。',
        subtitle: 'すべての通り、カフェ、思い出には音があります。曲をドロップして、それがどこに属するかを見てみましょう。',
        button1: 'ミックス生成',
        button2: 'トラックアップロード',
        cta: '無料で始める',
        note: 'クレジットカード不要 • 何千人もの音楽愛好家が参加中'
      },
      features: {
        title: '使い方',
        subtitle: 'あなたの音楽の旅を作るために必要なすべて',
        pinMusic: {
          title: '音楽をピン留め',
          description: 'マップ上のどこにでもピンを落として、YouTubeからお気に入りの曲を添付できます。すべての場所が音で物語を語ります。'
        },
        discover: {
          title: 'サウンドを発見',
          description: '世界中の人々が何を聴いているかを探索しましょう。地元のお気に入りから隠れた名曲まで、実在の場所に結び付けられた新しい音楽を発見できます。'
        },
        share: {
          title: 'シェア＆つながる',
          description: 'あなたの音楽の旅を作り、友達とシェアしましょう。旅行の思い出やお気に入りの場所のサウンドトラックマップを作成できます。'
        }
      },
      social: {
        title: 'コミュニティに参加',
        description: '何千人もの音楽愛好家がすでに彼らのお気に入りの音をマッピングしています。音楽と場所を祝うグローバルコミュニティの一員になりましょう。',
        stats: {
          users: 'アクティブユーザー',
          pins: '音楽ピン',
          countries: '国'
        }
      },
      cta: {
        title: 'あなたの音楽をマップしませんか？',
        description: '今日から音楽の旅を始めましょう。無料で楽しく、世界中の音とつながることができます。',
        button: 'マップを作成'
      },
      howItWorks: {
        title: 'シンプル。パワフル。グローバル。',
        step1: {
          title: 'マップをクリック',
          description: 'あなたにとって意味のある場所を選びましょう'
        },
        step2: {
          title: '曲を追加',
          description: 'YouTubeのリンクを貼り付けてストーリーを追加'
        },
        step3: {
          title: 'シェア＆発見',
          description: '世界中の音楽を探索しましょう'
        }
      }
    },
    footer: {
      copyright: '© 2025 MapSound. あなたの音楽、あなたの世界 — ロサンゼルスより愛を込めて ❤️'
    },
    auth: {
      backHome: 'ホームに戻る',
      title: 'MapSound',
      subtitle: '世界中にお気に入りの音楽をシェアしよう',
      username: 'ユーザー名',
      email: 'メールアドレス',
      password: 'パスワード',
      signIn: 'ログイン',
      signUp: '新規登録',
      loading: '読み込み中...',
      hasAccount: 'アカウントをお持ちですか？ログイン',
      noAccount: 'アカウントをお持ちでないですか？新規登録'
    },
    header: {
      search: '場所を検索...',
      logout: 'ログアウト'
    },
    sidebar: {
      map: 'マップ',
      myPins: 'マイドロップ',
      discover: '発見',
      nearby: '付近'
    },
    createPin: {
      title: 'サウンドドロップを作成',
      location: '場所',
      pinTitle: 'タイトル',
      titlePlaceholder: 'このサウンドドロップに名前を付ける',
      description: '説明',
      descriptionPlaceholder: 'このサウンドドロップについて説明...',
      youtubeUrl: 'YouTube URL',
      youtubeUrlPlaceholder: 'https://www.youtube.com/watch?v=...',
      tags: 'タグ',
      tagsPlaceholder: 'ロック, ジャズ, アンビエント（カンマ区切り）',
      cancel: 'キャンセル',
      create: 'ドロップを作成',
      creating: '作成中...'
    },
    editPin: {
      title: 'サウンドドロップを編集',
      update: 'ドロップを更新',
      updating: '更新中...',
      delete: 'ドロップを削除',
      deleting: '削除中...',
      confirmDelete: '本当にこのサウンドドロップを削除しますか？'
    },
    map: {
      loading: 'オーディオピンを読み込み中...',
      error: 'マップの読み込みに失敗しました'
    },
    myPins: {
      title: 'マイサウンドドロップ',
      empty: 'まだサウンドドロップを作成していません。マップをクリックして最初のドロップを作成しましょう！',
      plays: '再生'
    },
    discover: {
      title: 'サウンドドロップを発見',
      empty: 'サウンドドロップが見つかりません。最初に作成してみましょう！',
      plays: '再生'
    },
    nearby: {
      title: '付近のサウンドドロップ',
      empty: '付近にサウンドドロップがありません。歩き回って音楽を発見しましょう！',
      distance: '先',
      enableLocation: '位置情報を有効にする',
      locationDisabled: '位置情報アクセスが無効です',
      locationUnavailable: '位置情報が利用できません',
      searchingLocation: '位置を検索中...',
      within50m: '50m以内'
    }
  },
  ko: {
    landing: {
      hero: {
        title1: '지도에',
        title2: '비트를',
        title3: '드롭하세요.',
        subtitle: '모든 거리, 카페, 추억에는 소리가 있습니다. 노래를 드롭하고 그것이 속한 곳으로 퍼져나가는 것을 지켜보세요.',
        button1: '믹스 생성',
        button2: '트랙 업로드',
        cta: '무료로 시작하기',
        note: '신용카드 불필요 • 수천 명의 음악 애호가가 참여 중'
      },
      features: {
        title: '사용 방법',
        subtitle: '음악 여정을 만드는 데 필요한 모든 것',
        pinMusic: {
          title: '음악 핀 고정',
          description: '지도 어디든 핀을 놓고 YouTube에서 좋아하는 곡을 첨부하세요. 모든 장소가 소리로 이야기를 전합니다.'
        },
        discover: {
          title: '사운드 발견',
          description: '전 세계 사람들이 무엇을 듣고 있는지 탐험하세요. 지역 인기곡부터 숨겨진 보석까지, 실제 장소와 연결된 새로운 음악을 발견하세요.'
        },
        share: {
          title: '공유 & 연결',
          description: '음악 여정을 만들고 친구들과 공유하세요. 여행, 추억, 좋아하는 장소의 사운드트랙 지도를 만드세요.'
        }
      },
      social: {
        title: '커뮤니티에 참여하세요',
        description: '수천 명의 음악 애호가들이 이미 그들의 좋아하는 소리를 매핑하고 있습니다. 음악과 장소를 기념하는 글로벌 커뮤니티의 일원이 되세요.',
        stats: {
          users: '활성 사용자',
          pins: '음악 핀',
          countries: '국가'
        }
      },
      cta: {
        title: '음악 지도를 만들 준비가 되셨나요?',
        description: '오늘 음악 여정을 시작하세요. 무료이고 재미있으며 전 세계의 소리와 연결됩니다.',
        button: '지도 만들기'
      },
      howItWorks: {
        title: '간단함. 강력함. 글로벌.',
        step1: {
          title: '지도 클릭',
          description: '의미 있는 장소를 선택하세요'
        },
        step2: {
          title: '곡 추가',
          description: 'YouTube 링크를 붙여넣고 이야기를 추가하세요'
        },
        step3: {
          title: '공유 & 발견',
          description: '전 세계의 음악을 탐험하세요'
        }
      }
    },
    footer: {
      copyright: '© 2025 MapSound. 당신의 음악, 당신의 세계 — 로스앤젤레스에서 ❤️ 로 제작'
    },
    auth: {
      backHome: '홈으로 돌아가기',
      title: 'MapSound',
      subtitle: '전 세계에 좋아하는 음악을 공유하세요',
      username: '사용자 이름',
      email: '이메일',
      password: '비밀번호',
      signIn: '로그인',
      signUp: '회원가입',
      loading: '로딩 중...',
      hasAccount: '이미 계정이 있으신가요? 로그인',
      noAccount: '계정이 없으신가요? 회원가입'
    },
    header: {
      search: '위치 검색...',
      logout: '로그아웃'
    },
    sidebar: {
      map: '지도',
      myPins: '내 드롭',
      discover: '발견',
      nearby: '근처'
    },
    createPin: {
      title: '사운드 드롭 만들기',
      location: '위치',
      pinTitle: '제목',
      titlePlaceholder: '사운드 드롭 이름 지정',
      description: '설명',
      descriptionPlaceholder: '사운드 드롭 설명...',
      youtubeUrl: 'YouTube URL',
      youtubeUrlPlaceholder: 'https://www.youtube.com/watch?v=...',
      tags: '태그',
      tagsPlaceholder: '록, 재즈, 앰비언트 (쉼표로 구분)',
      cancel: '취소',
      create: '드롭 만들기',
      creating: '만드는 중...'
    },
    editPin: {
      title: '사운드 드롭 편집',
      update: '드롭 업데이트',
      updating: '업데이트 중...',
      delete: '드롭 삭제',
      deleting: '삭제 중...',
      confirmDelete: '정말로 이 사운드 드롭을 삭제하시겠습니까?'
    },
    map: {
      loading: '오디오 핀 로딩 중...',
      error: '지도 로드 실패'
    },
    myPins: {
      title: '내 사운드 드롭',
      empty: '아직 사운드 드롭을 만들지 않았습니다. 지도를 클릭하여 첫 번째 드롭을 만드세요!',
      plays: '재생'
    },
    discover: {
      title: '사운드 드롭 발견',
      empty: '사운드 드롭을 찾을 수 없습니다. 첫 번째로 만들어보세요!',
      plays: '재생'
    },
    nearby: {
      title: '근처 사운드 드롭',
      empty: '근처에 사운드 드롭이 없습니다. 걸어다니며 음악을 발견하세요!',
      distance: '거리',
      enableLocation: '위치 활성화',
      locationDisabled: '위치 액세스가 비활성화됨',
      locationUnavailable: '위치를 사용할 수 없음',
      searchingLocation: '위치 검색 중...',
      within50m: '50m 이내'
    }
  },
  es: {
    landing: {
      hero: {
        title1: 'Suelta',
        title2: 'ritmos',
        title3: 'en el mapa.',
        subtitle: 'Cada calle, café y recuerdo tiene un sonido. Suelta una canción y observa cómo se expande donde pertenece.',
        button1: 'Generar Mix',
        button2: 'Subir Pista',
        cta: 'Comenzar gratis',
        note: 'Sin tarjeta de crédito • Únete a miles de amantes de la música'
      },
      features: {
        title: 'Cómo funciona',
        subtitle: 'Todo lo que necesitas para crear tu viaje musical',
        pinMusic: {
          title: 'Ancla tu música',
          description: 'Coloca un pin en cualquier lugar del mapa y adjunta tus canciones favoritas de YouTube. Cada ubicación cuenta una historia a través del sonido.'
        },
        discover: {
          title: 'Descubre sonidos',
          description: 'Explora lo que otros están escuchando en todo el mundo. Desde favoritos locales hasta joyas escondidas, descubre nueva música vinculada a lugares reales.'
        },
        share: {
          title: 'Comparte y conecta',
          description: 'Construye tu viaje musical y compártelo con amigos. Crea un mapa sonoro de tus viajes, recuerdos y lugares favoritos.'
        }
      },
      social: {
        title: 'Únete a la comunidad',
        description: 'Miles de amantes de la música ya están mapeando sus sonidos favoritos. Sé parte de una comunidad global que celebra la música y los lugares.',
        stats: {
          users: 'Usuarios activos',
          pins: 'Pins musicales',
          countries: 'Países'
        }
      },
      cta: {
        title: '¿Listo para mapear tu música?',
        description: 'Comienza tu viaje musical hoy. Es gratis, divertido y te conecta con sonidos de todo el mundo.',
        button: 'Crea tu mapa'
      },
      howItWorks: {
        title: 'Simple. Potente. Global.',
        step1: {
          title: 'Haz clic en el mapa',
          description: 'Elige cualquier ubicación que signifique algo para ti'
        },
        step2: {
          title: 'Añade tu canción',
          description: 'Pega un enlace de YouTube y añade tu historia'
        },
        step3: {
          title: 'Comparte y descubre',
          description: 'Explora música de todo el mundo'
        }
      }
    },
    footer: {
      copyright: '© 2025 MapSound. Tu música, tu mundo — hecho con ❤️ en Los Ángeles.'
    },
    auth: {
      backHome: 'Volver al inicio',
      title: 'MapSound',
      subtitle: 'Comparte tu música favorita en todo el mundo',
      username: 'Nombre de usuario',
      email: 'Correo electrónico',
      password: 'Contraseña',
      signIn: 'Iniciar sesión',
      signUp: 'Registrarse',
      loading: 'Cargando...',
      hasAccount: '¿Ya tienes una cuenta? Inicia sesión',
      noAccount: '¿No tienes una cuenta? Regístrate'
    },
    header: {
      search: 'Buscar ubicaciones...',
      logout: 'Cerrar sesión'
    },
    sidebar: {
      map: 'Mapa',
      myPins: 'Mis drops',
      discover: 'Descubrir',
      nearby: 'Cerca'
    },
    createPin: {
      title: 'Crear sound drop',
      location: 'Ubicación',
      pinTitle: 'Título',
      titlePlaceholder: 'Nombra este sound drop',
      description: 'Descripción',
      descriptionPlaceholder: 'Describe este sound drop...',
      youtubeUrl: 'URL de YouTube',
      youtubeUrlPlaceholder: 'https://www.youtube.com/watch?v=...',
      tags: 'Etiquetas',
      tagsPlaceholder: 'rock, jazz, ambient (separadas por comas)',
      cancel: 'Cancelar',
      create: 'Crear drop',
      creating: 'Creando...'
    },
    editPin: {
      title: 'Editar sound drop',
      update: 'Actualizar drop',
      updating: 'Actualizando...',
      delete: 'Eliminar drop',
      deleting: 'Eliminando...',
      confirmDelete: '¿Estás seguro de que quieres eliminar este sound drop?'
    },
    map: {
      loading: 'Cargando pins de audio...',
      error: 'Error al cargar el mapa'
    },
    myPins: {
      title: 'Mis sound drops',
      empty: 'Aún no has creado ningún sound drop. ¡Haz clic en el mapa para crear el primero!',
      plays: 'reproducciones'
    },
    discover: {
      title: 'Descubrir sound drops',
      empty: 'No se encontraron sound drops. ¡Sé el primero en crear uno!',
      plays: 'reproducciones'
    },
    nearby: {
      title: 'Sound drops cercanos',
      empty: 'No hay sound drops cerca. ¡Camina para descubrir música!',
      distance: 'de distancia',
      enableLocation: 'Activar ubicación',
      locationDisabled: 'Acceso a ubicación desactivado',
      locationUnavailable: 'Ubicación no disponible',
      searchingLocation: 'Buscando tu ubicación...',
      within50m: 'Dentro de 50m'
    }
  },
  fr: {
    landing: {
      hero: {
        title1: 'Déposez des',
        title2: 'rythmes',
        title3: 'sur la carte.',
        subtitle: 'Chaque rue, café et souvenir a un son. Déposez une chanson et regardez-la se propager là où elle appartient.',
        button1: 'Générer un Mix',
        button2: 'Télécharger une Piste',
        cta: 'Commencer gratuitement',
        note: 'Aucune carte de crédit requise • Rejoignez des milliers d\'amateurs de musique'
      },
      features: {
        title: 'Comment ça marche',
        subtitle: 'Tout ce dont vous avez besoin pour créer votre parcours musical',
        pinMusic: {
          title: 'Épinglez votre musique',
          description: 'Placez une épingle n\'importe où sur la carte et attachez vos chansons préférées de YouTube. Chaque lieu raconte une histoire à travers le son.'
        },
        discover: {
          title: 'Découvrez des sons',
          description: 'Explorez ce que les autres écoutent dans le monde. Des favoris locaux aux pépites cachées, découvrez de la nouvelle musique liée à des lieux réels.'
        },
        share: {
          title: 'Partagez et connectez',
          description: 'Construisez votre parcours musical et partagez-le avec vos amis. Créez une carte sonore de vos voyages, souvenirs et lieux préférés.'
        }
      },
      social: {
        title: 'Rejoignez la communauté',
        description: 'Des milliers d\'amateurs de musique cartographient déjà leurs sons préférés. Faites partie d\'une communauté mondiale qui célèbre la musique et les lieux.',
        stats: {
          users: 'Utilisateurs actifs',
          pins: 'Épingles musicales',
          countries: 'Pays'
        }
      },
      cta: {
        title: 'Prêt à cartographier votre musique?',
        description: 'Commencez votre parcours musical aujourd\'hui. C\'est gratuit, amusant et vous connecte aux sons du monde entier.',
        button: 'Créer votre carte'
      },
      howItWorks: {
        title: 'Simple. Puissant. Global.',
        step1: {
          title: 'Cliquez sur la carte',
          description: 'Choisissez un lieu qui signifie quelque chose pour vous'
        },
        step2: {
          title: 'Ajoutez votre chanson',
          description: 'Collez un lien YouTube et ajoutez votre histoire'
        },
        step3: {
          title: 'Partagez et découvrez',
          description: 'Explorez la musique du monde entier'
        }
      }
    },
    footer: {
      copyright: '© 2025 MapSound. Votre musique, votre monde — fait avec ❤️ à Los Angeles.'
    },
    auth: {
      backHome: 'Retour à l\'accueil',
      title: 'MapSound',
      subtitle: 'Partagez votre musique préférée dans le monde entier',
      username: 'Nom d\'utilisateur',
      email: 'E-mail',
      password: 'Mot de passe',
      signIn: 'Se connecter',
      signUp: 'S\'inscrire',
      loading: 'Chargement...',
      hasAccount: 'Vous avez déjà un compte? Connectez-vous',
      noAccount: 'Vous n\'avez pas de compte? Inscrivez-vous'
    },
    header: {
      search: 'Rechercher des lieux...',
      logout: 'Déconnexion'
    },
    sidebar: {
      map: 'Carte',
      myPins: 'Mes drops',
      discover: 'Découvrir',
      nearby: 'À proximité'
    },
    createPin: {
      title: 'Créer un sound drop',
      location: 'Lieu',
      pinTitle: 'Titre',
      titlePlaceholder: 'Nommez ce sound drop',
      description: 'Description',
      descriptionPlaceholder: 'Décrivez ce sound drop...',
      youtubeUrl: 'URL YouTube',
      youtubeUrlPlaceholder: 'https://www.youtube.com/watch?v=...',
      tags: 'Tags',
      tagsPlaceholder: 'rock, jazz, ambient (séparés par des virgules)',
      cancel: 'Annuler',
      create: 'Créer un drop',
      creating: 'Création...'
    },
    editPin: {
      title: 'Modifier le sound drop',
      update: 'Mettre à jour le drop',
      updating: 'Mise à jour...',
      delete: 'Supprimer le drop',
      deleting: 'Suppression...',
      confirmDelete: 'Êtes-vous sûr de vouloir supprimer ce sound drop?'
    },
    map: {
      loading: 'Chargement des épingles audio...',
      error: 'Échec du chargement de la carte'
    },
    myPins: {
      title: 'Mes sound drops',
      empty: 'Vous n\'avez pas encore créé de sound drops. Cliquez sur la carte pour créer le premier!',
      plays: 'lectures'
    },
    discover: {
      title: 'Découvrir des sound drops',
      empty: 'Aucun sound drop trouvé. Soyez le premier à en créer un!',
      plays: 'lectures'
    },
    nearby: {
      title: 'Sound drops à proximité',
      empty: 'Aucun sound drop à proximité. Promenez-vous pour découvrir de la musique!',
      distance: 'de distance',
      enableLocation: 'Activer la localisation',
      locationDisabled: 'Accès à la localisation désactivé',
      locationUnavailable: 'Localisation non disponible',
      searchingLocation: 'Recherche de votre position...',
      within50m: 'Dans les 50m'
    }
  },
  de: {
    landing: {
      hero: {
        title1: 'Lass',
        title2: 'Beats',
        title3: 'auf die Karte fallen.',
        subtitle: 'Jede Straße, jedes Café und jede Erinnerung hat einen Klang. Lass einen Song fallen und beobachte, wie er sich ausbreitet, wo er hingehört.',
        button1: 'Mix generieren',
        button2: 'Track hochladen',
        cta: 'Kostenlos starten',
        note: 'Keine Kreditkarte erforderlich • Schließe dich Tausenden von Musikliebhabern an'
      },
      features: {
        title: 'So funktioniert es',
        subtitle: 'Alles, was du brauchst, um deine musikalische Reise zu gestalten',
        pinMusic: {
          title: 'Pinne deine Musik',
          description: 'Setze einen Pin irgendwo auf der Karte und füge deine Lieblingssongs von YouTube hinzu. Jeder Ort erzählt eine Geschichte durch Klang.'
        },
        discover: {
          title: 'Entdecke Klänge',
          description: 'Erkunde, was andere auf der Welt hören. Von lokalen Favoriten bis zu versteckten Schätzen, entdecke neue Musik, die mit echten Orten verbunden ist.'
        },
        share: {
          title: 'Teilen & Verbinden',
          description: 'Baue deine musikalische Reise auf und teile sie mit Freunden. Erstelle eine Soundtrack-Karte deiner Reisen, Erinnerungen und Lieblingsorte.'
        }
      },
      social: {
        title: 'Tritt der Community bei',
        description: 'Tausende Musikliebhaber kartieren bereits ihre Lieblingsklänge. Werde Teil einer globalen Community, die Musik und Orte feiert.',
        stats: {
          users: 'Aktive Benutzer',
          pins: 'Musik-Pins',
          countries: 'Länder'
        }
      },
      cta: {
        title: 'Bereit, deine Musik zu kartieren?',
        description: 'Starte heute deine musikalische Reise. Es ist kostenlos, macht Spaß und verbindet dich mit Klängen aus der ganzen Welt.',
        button: 'Erstelle deine Karte'
      },
      howItWorks: {
        title: 'Einfach. Leistungsstark. Global.',
        step1: {
          title: 'Klicke auf die Karte',
          description: 'Wähle einen Ort, der dir etwas bedeutet'
        },
        step2: {
          title: 'Füge deinen Song hinzu',
          description: 'Füge einen YouTube-Link ein und erzähle deine Geschichte'
        },
        step3: {
          title: 'Teilen & Entdecken',
          description: 'Erkunde Musik aus der ganzen Welt'
        }
      }
    },
    footer: {
      copyright: '© 2025 MapSound. Deine Musik, deine Welt — mit ❤️ in Los Angeles gemacht.'
    },
    auth: {
      backHome: 'Zurück zur Startseite',
      title: 'MapSound',
      subtitle: 'Teile deine Lieblingsmusik auf der ganzen Welt',
      username: 'Benutzername',
      email: 'E-Mail',
      password: 'Passwort',
      signIn: 'Anmelden',
      signUp: 'Registrieren',
      loading: 'Laden...',
      hasAccount: 'Haben Sie bereits ein Konto? Anmelden',
      noAccount: 'Noch kein Konto? Registrieren'
    },
    header: {
      search: 'Orte suchen...',
      logout: 'Abmelden'
    },
    sidebar: {
      map: 'Karte',
      myPins: 'Meine Drops',
      discover: 'Entdecken',
      nearby: 'In der Nähe'
    },
    createPin: {
      title: 'Sound Drop erstellen',
      location: 'Standort',
      pinTitle: 'Titel',
      titlePlaceholder: 'Benenne diesen Sound Drop',
      description: 'Beschreibung',
      descriptionPlaceholder: 'Beschreibe diesen Sound Drop...',
      youtubeUrl: 'YouTube-URL',
      youtubeUrlPlaceholder: 'https://www.youtube.com/watch?v=...',
      tags: 'Tags',
      tagsPlaceholder: 'Rock, Jazz, Ambient (durch Kommas getrennt)',
      cancel: 'Abbrechen',
      create: 'Drop erstellen',
      creating: 'Erstellen...'
    },
    editPin: {
      title: 'Sound Drop bearbeiten',
      update: 'Drop aktualisieren',
      updating: 'Aktualisieren...',
      delete: 'Drop löschen',
      deleting: 'Löschen...',
      confirmDelete: 'Sind Sie sicher, dass Sie diesen Sound Drop löschen möchten?'
    },
    map: {
      loading: 'Audio-Pins werden geladen...',
      error: 'Fehler beim Laden der Karte'
    },
    myPins: {
      title: 'Meine Sound Drops',
      empty: 'Du hast noch keine Sound Drops erstellt. Klicke auf die Karte, um deinen ersten zu erstellen!',
      plays: 'Wiedergaben'
    },
    discover: {
      title: 'Sound Drops entdecken',
      empty: 'Keine Sound Drops gefunden. Sei der Erste, der einen erstellt!',
      plays: 'Wiedergaben'
    },
    nearby: {
      title: 'Sound Drops in der Nähe',
      empty: 'Keine Sound Drops in der Nähe. Laufe herum, um Musik zu entdecken!',
      distance: 'entfernt',
      enableLocation: 'Standort aktivieren',
      locationDisabled: 'Standortzugriff deaktiviert',
      locationUnavailable: 'Standort nicht verfügbar',
      searchingLocation: 'Suche nach deinem Standort...',
      within50m: 'Innerhalb von 50m'
    }
  },
  it: {
    landing: {
      hero: {
        title1: 'Lascia cadere',
        title2: 'ritmi',
        title3: 'sulla mappa.',
        subtitle: 'Ogni strada, caffè e ricordo ha un suono. Lascia cadere una canzone e guardala espandersi dove appartiene.',
        button1: 'Genera Mix',
        button2: 'Carica Traccia',
        cta: 'Inizia gratis',
        note: 'Nessuna carta di credito richiesta • Unisciti a migliaia di amanti della musica'
      },
      features: {
        title: 'Come funziona',
        subtitle: 'Tutto ciò di cui hai bisogno per creare il tuo viaggio musicale',
        pinMusic: {
          title: 'Fissa la tua musica',
          description: 'Metti un pin ovunque sulla mappa e allega le tue canzoni preferite da YouTube. Ogni luogo racconta una storia attraverso il suono.'
        },
        discover: {
          title: 'Scopri suoni',
          description: 'Esplora cosa stanno ascoltando gli altri nel mondo. Dai preferiti locali alle gemme nascoste, scopri nuova musica legata a luoghi reali.'
        },
        share: {
          title: 'Condividi e connetti',
          description: 'Costruisci il tuo viaggio musicale e condividilo con gli amici. Crea una mappa sonora dei tuoi viaggi, ricordi e luoghi preferiti.'
        }
      },
      social: {
        title: 'Unisciti alla comunità',
        description: 'Migliaia di amanti della musica stanno già mappando i loro suoni preferiti. Fai parte di una comunità globale che celebra la musica e i luoghi.',
        stats: {
          users: 'Utenti attivi',
          pins: 'Pin musicali',
          countries: 'Paesi'
        }
      },
      cta: {
        title: 'Pronto a mappare la tua musica?',
        description: 'Inizia il tuo viaggio musicale oggi. È gratis, divertente e ti connette ai suoni di tutto il mondo.',
        button: 'Crea la tua mappa'
      },
      howItWorks: {
        title: 'Semplice. Potente. Globale.',
        step1: {
          title: 'Clicca sulla mappa',
          description: 'Scegli un luogo che significhi qualcosa per te'
        },
        step2: {
          title: 'Aggiungi la tua canzone',
          description: 'Incolla un link YouTube e aggiungi la tua storia'
        },
        step3: {
          title: 'Condividi e scopri',
          description: 'Esplora la musica da tutto il mondo'
        }
      }
    },
    footer: {
      copyright: '© 2025 MapSound. La tua musica, il tuo mondo — fatto con ❤️ a Los Angeles.'
    },
    auth: {
      backHome: 'Torna alla home',
      title: 'MapSound',
      subtitle: 'Condividi la tua musica preferita in tutto il mondo',
      username: 'Nome utente',
      email: 'Email',
      password: 'Password',
      signIn: 'Accedi',
      signUp: 'Registrati',
      loading: 'Caricamento...',
      hasAccount: 'Hai già un account? Accedi',
      noAccount: 'Non hai un account? Registrati'
    },
    header: {
      search: 'Cerca luoghi...',
      logout: 'Disconnetti'
    },
    sidebar: {
      map: 'Mappa',
      myPins: 'I miei drop',
      discover: 'Scopri',
      nearby: 'Vicino'
    },
    createPin: {
      title: 'Crea sound drop',
      location: 'Posizione',
      pinTitle: 'Titolo',
      titlePlaceholder: 'Dai un nome a questo sound drop',
      description: 'Descrizione',
      descriptionPlaceholder: 'Descrivi questo sound drop...',
      youtubeUrl: 'URL YouTube',
      youtubeUrlPlaceholder: 'https://www.youtube.com/watch?v=...',
      tags: 'Tag',
      tagsPlaceholder: 'rock, jazz, ambient (separati da virgole)',
      cancel: 'Annulla',
      create: 'Crea drop',
      creating: 'Creazione...'
    },
    editPin: {
      title: 'Modifica sound drop',
      update: 'Aggiorna drop',
      updating: 'Aggiornamento...',
      delete: 'Elimina drop',
      deleting: 'Eliminazione...',
      confirmDelete: 'Sei sicuro di voler eliminare questo sound drop?'
    },
    map: {
      loading: 'Caricamento pin audio...',
      error: 'Errore nel caricamento della mappa'
    },
    myPins: {
      title: 'I miei sound drop',
      empty: 'Non hai ancora creato sound drop. Clicca sulla mappa per creare il primo!',
      plays: 'riproduzioni'
    },
    discover: {
      title: 'Scopri sound drop',
      empty: 'Nessun sound drop trovato. Sii il primo a crearne uno!',
      plays: 'riproduzioni'
    },
    nearby: {
      title: 'Sound drop vicini',
      empty: 'Nessun sound drop nelle vicinanze. Cammina per scoprire musica!',
      distance: 'di distanza',
      enableLocation: 'Abilita posizione',
      locationDisabled: 'Accesso alla posizione disabilitato',
      locationUnavailable: 'Posizione non disponibile',
      searchingLocation: 'Ricerca della tua posizione...',
      within50m: 'Entro 50m'
    }
  },
  pt: {
    landing: {
      hero: {
        title1: 'Solte',
        title2: 'batidas',
        title3: 'no mapa.',
        subtitle: 'Cada rua, café e memória tem um som. Solte uma música e assista ela se espalhar onde pertence.',
        button1: 'Gerar Mix',
        button2: 'Enviar Faixa',
        cta: 'Começar grátis',
        note: 'Sem cartão de crédito necessário • Junte-se a milhares de amantes da música'
      },
      features: {
        title: 'Como funciona',
        subtitle: 'Tudo o que você precisa para criar sua jornada musical',
        pinMusic: {
          title: 'Fixe sua música',
          description: 'Coloque um alfinete em qualquer lugar do mapa e anexe suas músicas favoritas do YouTube. Cada local conta uma história através do som.'
        },
        discover: {
          title: 'Descubra sons',
          description: 'Explore o que outros estão ouvindo ao redor do mundo. De favoritos locais a joias escondidas, descubra novas músicas ligadas a lugares reais.'
        },
        share: {
          title: 'Compartilhe e conecte',
          description: 'Construa sua jornada musical e compartilhe com amigos. Crie um mapa sonoro de suas viagens, memórias e lugares favoritos.'
        }
      },
      social: {
        title: 'Junte-se à comunidade',
        description: 'Milhares de amantes da música já estão mapeando seus sons favoritos. Faça parte de uma comunidade global que celebra música e lugares.',
        stats: {
          users: 'Usuários ativos',
          pins: 'Pins musicais',
          countries: 'Países'
        }
      },
      cta: {
        title: 'Pronto para mapear sua música?',
        description: 'Comece sua jornada musical hoje. É grátis, divertido e conecta você a sons de todo o mundo.',
        button: 'Crie seu mapa'
      },
      howItWorks: {
        title: 'Simples. Poderoso. Global.',
        step1: {
          title: 'Clique no mapa',
          description: 'Escolha um local que significa algo para você'
        },
        step2: {
          title: 'Adicione sua música',
          description: 'Cole um link do YouTube e adicione sua história'
        },
        step3: {
          title: 'Compartilhe e descubra',
          description: 'Explore música de todo o mundo'
        }
      }
    },
    footer: {
      copyright: '© 2025 MapSound. Sua música, seu mundo — feito com ❤️ em Los Angeles.'
    },
    auth: {
      backHome: 'Voltar ao início',
      title: 'MapSound',
      subtitle: 'Compartilhe sua música favorita ao redor do mundo',
      username: 'Nome de usuário',
      email: 'E-mail',
      password: 'Senha',
      signIn: 'Entrar',
      signUp: 'Cadastrar',
      loading: 'Carregando...',
      hasAccount: 'Já tem uma conta? Entre',
      noAccount: 'Não tem uma conta? Cadastre-se'
    },
    header: {
      search: 'Buscar locais...',
      logout: 'Sair'
    },
    sidebar: {
      map: 'Mapa',
      myPins: 'Meus drops',
      discover: 'Descobrir',
      nearby: 'Próximo'
    },
    createPin: {
      title: 'Criar sound drop',
      location: 'Local',
      pinTitle: 'Título',
      titlePlaceholder: 'Nomeie este sound drop',
      description: 'Descrição',
      descriptionPlaceholder: 'Descreva este sound drop...',
      youtubeUrl: 'URL do YouTube',
      youtubeUrlPlaceholder: 'https://www.youtube.com/watch?v=...',
      tags: 'Tags',
      tagsPlaceholder: 'rock, jazz, ambient (separadas por vírgulas)',
      cancel: 'Cancelar',
      create: 'Criar drop',
      creating: 'Criando...'
    },
    editPin: {
      title: 'Editar sound drop',
      update: 'Atualizar drop',
      updating: 'Atualizando...',
      delete: 'Excluir drop',
      deleting: 'Excluindo...',
      confirmDelete: 'Tem certeza de que deseja excluir este sound drop?'
    },
    map: {
      loading: 'Carregando pins de áudio...',
      error: 'Falha ao carregar o mapa'
    },
    myPins: {
      title: 'Meus sound drops',
      empty: 'Você ainda não criou nenhum sound drop. Clique no mapa para criar o primeiro!',
      plays: 'reproduções'
    },
    discover: {
      title: 'Descobrir sound drops',
      empty: 'Nenhum sound drop encontrado. Seja o primeiro a criar um!',
      plays: 'reproduções'
    },
    nearby: {
      title: 'Sound drops próximos',
      empty: 'Nenhum sound drop por perto. Caminhe para descobrir música!',
      distance: 'de distância',
      enableLocation: 'Ativar localização',
      locationDisabled: 'Acesso à localização desativado',
      locationUnavailable: 'Localização não disponível',
      searchingLocation: 'Procurando sua localização...',
      within50m: 'Dentro de 50m'
    }
  },
  zh: {
    landing: {
      hero: {
        title1: '在地图上',
        title2: '投放',
        title3: '节拍。',
        subtitle: '每条街道、咖啡馆和回忆都有声音。投放一首歌，看着它在属于它的地方荡漾。',
        button1: '生成混音',
        button2: '上传曲目',
        cta: '免费开始',
        note: '无需信用卡 • 加入数千名音乐爱好者'
      },
      features: {
        title: '使用方法',
        subtitle: '创建音乐之旅所需的一切',
        pinMusic: {
          title: '固定你的音乐',
          description: '在地图上的任何地方放置一个图钉，并附上你最喜欢的YouTube歌曲。每个位置都通过声音讲述一个故事。'
        },
        discover: {
          title: '发现声音',
          description: '探索世界各地的人们在听什么。从本地热门到隐藏的宝石，发现与真实地点相关的新音乐。'
        },
        share: {
          title: '分享与连接',
          description: '构建你的音乐之旅并与朋友分享。创建你的旅行、回忆和最喜欢的地方的音轨地图。'
        }
      },
      social: {
        title: '加入社区',
        description: '成千上万的音乐爱好者已经在映射他们最喜欢的声音。成为庆祝音乐和地点的全球社区的一员。',
        stats: {
          users: '活跃用户',
          pins: '音乐图钉',
          countries: '国家'
        }
      },
      cta: {
        title: '准备好映射你的音乐了吗？',
        description: '今天开始你的音乐之旅。它是免费的、有趣的，并将你与世界各地的声音连接起来。',
        button: '创建你的地图'
      },
      howItWorks: {
        title: '简单。强大。全球化。',
        step1: {
          title: '点击地图',
          description: '选择一个对你有意义的位置'
        },
        step2: {
          title: '添加你的歌曲',
          description: '粘贴YouTube链接并添加你的故事'
        },
        step3: {
          title: '分享和发现',
          description: '探索来自世界各地的音乐'
        }
      }
    },
    footer: {
      copyright: '© 2025 MapSound. 你的音乐，你的世界 — 在洛杉矶用 ❤️ 制作。'
    },
    auth: {
      backHome: '返回首页',
      title: 'MapSound',
      subtitle: '在世界各地分享你最喜欢的音乐',
      username: '用户名',
      email: '电子邮件',
      password: '密码',
      signIn: '登录',
      signUp: '注册',
      loading: '加载中...',
      hasAccount: '已有账户？登录',
      noAccount: '没有账户？注册'
    },
    header: {
      search: '搜索位置...',
      logout: '退出'
    },
    sidebar: {
      map: '地图',
      myPins: '我的drops',
      discover: '发现',
      nearby: '附近'
    },
    createPin: {
      title: '创建声音drop',
      location: '位置',
      pinTitle: '标题',
      titlePlaceholder: '命名此声音drop',
      description: '描述',
      descriptionPlaceholder: '描述此声音drop...',
      youtubeUrl: 'YouTube链接',
      youtubeUrlPlaceholder: 'https://www.youtube.com/watch?v=...',
      tags: '标签',
      tagsPlaceholder: '摇滚, 爵士, 环境音乐（逗号分隔）',
      cancel: '取消',
      create: '创建drop',
      creating: '创建中...'
    },
    editPin: {
      title: '编辑声音drop',
      update: '更新drop',
      updating: '更新中...',
      delete: '删除drop',
      deleting: '删除中...',
      confirmDelete: '确定要删除此声音drop吗？'
    },
    map: {
      loading: '加载音频图钉中...',
      error: '加载地图失败'
    },
    myPins: {
      title: '我的声音drops',
      empty: '你还没有创建任何声音drop。点击地图创建第一个！',
      plays: '播放次数'
    },
    discover: {
      title: '发现声音drops',
      empty: '未找到声音drop。成为第一个创建的人！',
      plays: '播放次数'
    },
    nearby: {
      title: '附近的声音drops',
      empty: '附近没有声音drop。四处走走发现音乐！',
      distance: '远',
      enableLocation: '启用位置',
      locationDisabled: '位置访问已禁用',
      locationUnavailable: '位置不可用',
      searchingLocation: '正在搜索你的位置...',
      within50m: '50米内'
    }
  }
};
